/**
 * Pricing page OAuth and RevenueCat payment link integration
 * Handles SSO login (Google/Microsoft) and redirects to RevenueCat payment
 */

/** Centralized pricing configuration - update prices here */
const PRICING_CONFIG = {
  monthly: {
    price: 9.99,
    currency: 'USD',
    symbol: '$',
    period: 'month',
    periodShort: 'mo',
  },
  yearly: {
    price: 59.88,
    currency: 'USD',
    symbol: '$',
    period: 'year',
    periodShort: 'yr',
  },
} as const;

/** Calculated values from pricing config */
const PRICING_CALCULATED = {
  yearlyMonthlyEquivalent: PRICING_CONFIG.yearly.price / 12,
  yearlySavingsAmount: (PRICING_CONFIG.monthly.price * 12) - PRICING_CONFIG.yearly.price,
  yearlySavingsPercent: Math.round(
    (1 - PRICING_CONFIG.yearly.price / (PRICING_CONFIG.monthly.price * 12)) * 100
  ),
  dailyPrice: PRICING_CONFIG.monthly.price / 30,
  monthlyFullYearPrice: PRICING_CONFIG.monthly.price * 12,
} as const;

/** OAuth client IDs */
const OAUTH_CONFIG = {
  google: {
    clientId: '167102563798-ad8u9dku500vkpcr0jl5ms9ba79ml80k.apps.googleusercontent.com',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
    scopes: ['openid', 'email', 'profile'],
  },
  microsoft: {
    clientId: '8d38b4d2-767a-46bc-bbf3-1b557b6ada0e',
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize',
    scopes: ['openid', 'email', 'profile'],
  },
} as const;

/** RevenueCat payment link base URL */
const REVENUECAT_PAYMENT_URL = 'https://pay.rev.cat/cnmyzcmdochegkhr';

/** User info from OAuth */
interface UserInfo {
  readonly email: string;
  readonly name: string | null;
  readonly emailVerified?: boolean;
}

/** Plan type */
type PlanType = 'monthly' | 'yearly';

/** Current state */
let currentPlan: PlanType | null = null;
let currentUser: UserInfo | null = null;

/**
 * Decodes a JWT token payload (base64url encoded) with proper UTF-8 support
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format');
  }

  const payload = parts[1];
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const binaryString = atob(padded);

  // Convert binary string to UTF-8 properly
  const bytes = Uint8Array.from(binaryString, (char) => char.charCodeAt(0));
  const decoded = new TextDecoder('utf-8').decode(bytes);

  return JSON.parse(decoded) as Record<string, unknown>;
}

/**
 * Generates a random nonce for OAuth
 */
function generateNonce(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Opens OAuth popup and waits for callback
 */
function openOAuthPopup(url: string): Promise<Record<string, string>> {
  return new Promise((resolve, reject) => {
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'oauth-popup',
      `width=${width},height=${height},left=${left},top=${top},popup=yes`
    );

    if (!popup) {
      reject(new Error('Failed to open popup. Please allow popups for this site.'));
      return;
    }

    const handleMessage = (event: MessageEvent): void => {
      if (event.origin !== window.location.origin) {
        return;
      }

      const data = event.data as { type?: string; params?: Record<string, string> };
      if (data.type === 'oauth-callback' && data.params) {
        window.removeEventListener('message', handleMessage);
        resolve(data.params);
      }
    };

    window.addEventListener('message', handleMessage);

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener('message', handleMessage);
        reject(new Error('Login cancelled'));
      }
    }, 500);
  });
}

/**
 * Logs in with Google OAuth
 */
async function loginWithGoogle(): Promise<UserInfo> {
  const { clientId, authUrl, scopes } = OAUTH_CONFIG.google;
  const redirectUri = `${window.location.origin}/oauth-callback.html`;
  const nonce = generateNonce();

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'id_token',
    scope: scopes.join(' '),
    nonce: nonce,
    prompt: 'select_account',
  });

  const oauthUrl = `${authUrl}?${params.toString()}`;
  const result = await openOAuthPopup(oauthUrl);

  if (result.error) {
    throw new Error(result.error_description || result.error);
  }

  const idToken = result.id_token;
  if (!idToken) {
    throw new Error('No ID token received from Google');
  }

  const payload = decodeJwtPayload(idToken);

  const email = payload.email as string | undefined;
  if (!email) {
    throw new Error('No email in Google token');
  }

  return {
    email,
    name: (payload.name as string) || null,
    emailVerified: payload.email_verified as boolean | undefined,
  };
}

/**
 * Logs in with Microsoft OAuth
 */
async function loginWithMicrosoft(): Promise<UserInfo> {
  const { clientId, authUrl, scopes } = OAUTH_CONFIG.microsoft;
  const redirectUri = `${window.location.origin}/oauth-callback.html`;
  const nonce = generateNonce();

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'id_token',
    scope: scopes.join(' '),
    response_mode: 'fragment',
    nonce: nonce,
    prompt: 'select_account',
  });

  const oauthUrl = `${authUrl}?${params.toString()}`;
  const result = await openOAuthPopup(oauthUrl);

  if (result.error) {
    throw new Error(result.error_description || result.error);
  }

  const idToken = result.id_token;
  if (!idToken) {
    throw new Error('No ID token received from Microsoft');
  }

  const payload = decodeJwtPayload(idToken);

  const email = (payload.email as string) || (payload.preferred_username as string);
  if (!email) {
    throw new Error('No email in Microsoft token');
  }

  return {
    email,
    name: (payload.name as string) || null,
    emailVerified: payload.email_verified as boolean | undefined,
  };
}

/**
 * Validates user info (checks email verification if claim present)
 */
function validateUser(user: UserInfo): boolean {
  if (user.emailVerified === false) {
    return false;
  }
  return true;
}

/**
 * Redirects to RevenueCat payment page
 */
function proceedToPayment(email: string): void {
  const encodedEmail = encodeURIComponent(email);
  window.location.href = `${REVENUECAT_PAYMENT_URL}/${encodedEmail}`;
}

/**
 * Gets modal elements
 */
function getModalElements(): {
  modal: HTMLElement;
  loginStep: HTMLElement;
  confirmStep: HTMLElement;
  errorStep: HTMLElement;
  userEmail: HTMLElement;
  userName: HTMLElement;
  planName: HTMLElement;
  errorMessage: HTMLElement;
} {
  return {
    modal: document.getElementById('purchase-modal')!,
    loginStep: document.getElementById('modal-step-login')!,
    confirmStep: document.getElementById('modal-step-confirm')!,
    errorStep: document.getElementById('modal-step-error')!,
    userEmail: document.getElementById('modal-user-email')!,
    userName: document.getElementById('modal-user-name')!,
    planName: document.getElementById('modal-plan-name')!,
    errorMessage: document.getElementById('modal-error-message')!,
  };
}

/**
 * Shows the purchase modal
 */
function showModal(plan: PlanType): void {
  currentPlan = plan;
  currentUser = null;

  const elements = getModalElements();
  elements.loginStep.classList.remove('hidden');
  elements.confirmStep.classList.add('hidden');
  elements.errorStep.classList.add('hidden');
  elements.modal.classList.remove('hidden');

  document.body.style.overflow = 'hidden';
}

/**
 * Hides the purchase modal
 */
function hideModal(): void {
  const elements = getModalElements();
  elements.modal.classList.add('hidden');
  document.body.style.overflow = '';
  currentPlan = null;
  currentUser = null;
}

/**
 * Shows the confirm step with user info
 */
function showConfirmStep(user: UserInfo): void {
  currentUser = user;

  const elements = getModalElements();
  elements.userEmail.textContent = user.email;
  elements.userName.textContent = user.name || user.email;
  const { monthly, yearly } = PRICING_CONFIG;
  elements.planName.textContent = currentPlan === 'monthly'
    ? `Monthly (${monthly.symbol}${monthly.price}/${monthly.periodShort})`
    : `Yearly (${yearly.symbol}${yearly.price}/${yearly.periodShort})`;

  const userInitial = document.getElementById('modal-user-initial');
  if (userInitial) {
    const initial = (user.name || user.email).charAt(0).toUpperCase();
    userInitial.textContent = initial;
  }

  elements.loginStep.classList.add('hidden');
  elements.errorStep.classList.add('hidden');
  elements.confirmStep.classList.remove('hidden');
}

/**
 * Shows error in modal
 */
function showModalError(message: string): void {
  const elements = getModalElements();
  elements.errorMessage.textContent = message;

  elements.loginStep.classList.add('hidden');
  elements.confirmStep.classList.add('hidden');
  elements.errorStep.classList.remove('hidden');
}

/**
 * Handles Google login button click
 */
async function handleGoogleLogin(): Promise<void> {
  const btn = document.getElementById('btn-login-google') as HTMLButtonElement;
  const originalHtml = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="animate-pulse">Signing in...</span>';

  try {
    const user = await loginWithGoogle();

    if (!validateUser(user)) {
      showModalError('Please verify your email address before purchasing.');
      return;
    }

    showConfirmStep(user);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Login failed';
    if (message !== 'Login cancelled') {
      showModalError(message);
    }
  } finally {
    btn.disabled = false;
    btn.innerHTML = originalHtml;
  }
}

/**
 * Handles Microsoft login button click
 */
async function handleMicrosoftLogin(): Promise<void> {
  const btn = document.getElementById('btn-login-microsoft') as HTMLButtonElement;
  const originalHtml = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="animate-pulse">Signing in...</span>';

  try {
    const user = await loginWithMicrosoft();

    if (!validateUser(user)) {
      showModalError('Please verify your email address before purchasing.');
      return;
    }

    showConfirmStep(user);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Login failed';
    if (message !== 'Login cancelled') {
      showModalError(message);
    }
  } finally {
    btn.disabled = false;
    btn.innerHTML = originalHtml;
  }
}

/**
 * Handles proceed to payment button click
 */
function handleProceedToPayment(): void {
  if (!currentUser) {
    return;
  }

  proceedToPayment(currentUser.email);
}

/**
 * Handles back button click (return to login step)
 */
function handleBackToLogin(): void {
  const elements = getModalElements();
  elements.confirmStep.classList.add('hidden');
  elements.errorStep.classList.add('hidden');
  elements.loginStep.classList.remove('hidden');
}

/**
 * Updates all price displays on the page from the centralized config
 */
function updatePriceDisplays(): void {
  const { monthly, yearly } = PRICING_CONFIG;
  const { yearlyMonthlyEquivalent } = PRICING_CALCULATED;

  // Calculate yearly daily price
  const yearlyDailyPrice = yearlyMonthlyEquivalent / 30;

  // Helper to safely update text content
  const updateElement = (id: string, text: string): void => {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  };

  // Monthly price displays
  updateElement('price-monthly', `${monthly.symbol}${monthly.price}`);

  // Yearly price displays
  updateElement('price-yearly', `${yearly.symbol}${yearly.price}`);
  updateElement('price-yearly-monthly', `${yearly.symbol}${yearlyMonthlyEquivalent.toFixed(2)}`);
  updateElement('price-yearly-savings', 'Pay for 6 months, get 12');

  // Comparison section (uses yearly monthly equivalent)
  updateElement('price-yearly-coffee', `${yearly.symbol}${yearlyMonthlyEquivalent.toFixed(2)}/month`);
  updateElement('price-yearly-daily', `${yearly.symbol}${yearlyDailyPrice.toFixed(2)}/day`);
  updateElement('price-yearly-sanity', `${yearly.symbol}${yearlyMonthlyEquivalent.toFixed(2)}`);
}

/**
 * Initializes the pricing page
 */
function initPricingPage(): void {
  // Update all price displays from config
  updatePriceDisplays();

  // Buy buttons
  const btnMonthly = document.getElementById('btn-buy-monthly');
  const btnYearly = document.getElementById('btn-buy-yearly');

  btnMonthly?.addEventListener('click', () => showModal('monthly'));
  btnYearly?.addEventListener('click', () => showModal('yearly'));

  // Modal buttons
  const btnGoogle = document.getElementById('btn-login-google');
  const btnMicrosoft = document.getElementById('btn-login-microsoft');
  const btnProceed = document.getElementById('btn-proceed-payment');
  const btnBack = document.getElementById('btn-back-login');
  const btnRetry = document.getElementById('btn-retry-login');
  const btnClose = document.getElementById('btn-close-modal');
  const modalBackdrop = document.getElementById('modal-backdrop');

  btnGoogle?.addEventListener('click', handleGoogleLogin);
  btnMicrosoft?.addEventListener('click', handleMicrosoftLogin);
  btnProceed?.addEventListener('click', handleProceedToPayment);
  btnBack?.addEventListener('click', handleBackToLogin);
  btnRetry?.addEventListener('click', handleBackToLogin);
  btnClose?.addEventListener('click', hideModal);
  modalBackdrop?.addEventListener('click', hideModal);

  // ESC key to close modal
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      hideModal();
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPricingPage);
} else {
  initPricingPage();
}
